class CustomHomepageGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.originalSlides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = Number(this.dataset.interval) * 1000;
    this.activeIndex = 0;

    if (!this.track || this.originalSlides.length === 0) return;

    this.buildInfiniteTrack();

    this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
    this.track.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    });

    this.updateControls();
    requestAnimationFrame(() => {
      this.jumpTo(this.originalSlides.length);
      this.startAutoplay();
    });
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  buildInfiniteTrack() {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.originalSlides.forEach((slide, index) => {
      slide.dataset.galleryVisualSlide = '';
      slide.dataset.originalIndex = String(index);
      before.append(this.createClone(slide, index));
      after.append(this.createClone(slide, index));
    });

    this.track.prepend(before);
    this.track.append(after);
    this.visualSlides = Array.from(this.track.querySelectorAll('[data-gallery-visual-slide]'));
  }

  createClone(slide, index) {
    const clone = slide.cloneNode(true);
    clone.removeAttribute('data-gallery-slide');
    clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('data-gallery-visual-slide', '');
    clone.setAttribute('data-original-index', String(index));
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    return clone;
  }

  handleScroll() {
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      const trackStyle = getComputedStyle(this.track);
      const snapLeft = this.track.getBoundingClientRect().left + parseFloat(trackStyle.paddingLeft);
      let closestVisualIndex = 0;
      let closestDistance = Infinity;

      this.visualSlides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - snapLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestVisualIndex = index;
        }
      });

      this.activeIndex = Number(this.visualSlides[closestVisualIndex].dataset.originalIndex);
      this.updateControls();

      const slideCount = this.originalSlides.length;
      if (closestVisualIndex < slideCount || closestVisualIndex >= slideCount * 2) {
        this.jumpTo(slideCount + this.activeIndex);
      }
    }, 80);
  }

  goTo(index) {
    const slideCount = this.originalSlides.length;
    const targetIndex = (index + slideCount) % slideCount;
    let visualIndex = slideCount + targetIndex;

    if (index < 0) visualIndex = slideCount - 1;
    if (index >= slideCount) visualIndex = slideCount * 2;

    this.activeIndex = targetIndex;
    this.scrollToVisualSlide(visualIndex, true);
    this.updateControls();
    this.restartAutoplay();
  }

  scrollToVisualSlide(index, smooth) {
    const slide = this.visualSlides[index];
    if (!slide) return;

    const trackStyle = getComputedStyle(this.track);
    const paddingLeft = parseFloat(trackStyle.paddingLeft);
    const left = this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left - paddingLeft;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  }

  jumpTo(index) {
    this.scrollToVisualSlide(index, false);
  }

  updateControls() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  startAutoplay() {
    if (!this.autoplay || this.originalSlides.length < 2 || document.hidden) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.goTo(this.activeIndex + 1), this.interval);
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer);
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('custom-homepage-gallery')) {
  customElements.define('custom-homepage-gallery', CustomHomepageGallery);
}
